// AICA ErrorBoundary Component
// Catches React errors and displays a fallback UI with specific error messages
// Priority 2.2.3: Enhanced Error Boundaries with categorized errors

import { Component, ReactNode } from 'react';
import {
  AlertTriangle,
  RefreshCw,
  Home,
  WifiOff,
  Lock,
  Database,
  Bug,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { captureException } from '@/lib/sentry';
import { maskInvitationTokenInPath } from '@/lib/sentry-event-mask';
import { logError } from '@/lib/logger';
import i18n from '@/i18n/config';

/**
 * Error types for categorization
 */
type ErrorType = 'network' | 'auth' | 'data' | 'chunk' | 'unknown';

/**
 * Error metadata for better UX
 */
interface ErrorMetadata {
  type: ErrorType;
  title: string;
  message: string;
  icon: typeof AlertTriangle;
  iconColor: string;
  canRetry: boolean;
  canGoHome: boolean;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  /**
   * When this value changes, a boundary that is currently showing an error
   * resets itself. Pass the current route (e.g. `location.pathname`) so that
   * navigating away from a crashed screen clears the error instead of leaving
   * the fallback stuck on screen.
   */
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  errorMetadata?: ErrorMetadata;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  /**
   * Categorize error and provide helpful metadata
   */
  private categorizeError(error: Error): ErrorMetadata {
    const t = i18n.t.bind(i18n);
    const errorMessage = error.message.toLowerCase();

    // Chunk / dynamic-import loading errors (code splitting).
    // Checked FIRST: a failed dynamic import is phrased by the browser as
    // "Failed to fetch dynamically imported module" (Chrome) etc., which
    // contains "fetch" and would otherwise be misclassified as a network
    // problem and hide the reload action. Match the message only — the stack
    // may contain "chunk" from bundler/test paths.
    const CHUNK_SIGNATURES = [
      'chunk',
      'dynamically imported module',
      'module script failed',
      'unable to preload',
    ];
    if (CHUNK_SIGNATURES.some((signature) => errorMessage.includes(signature))) {
      return {
        type: 'chunk',
        title: t('errorBoundary.chunk.title'),
        message: t('errorBoundary.chunk.message'),
        icon: RefreshCw,
        iconColor: 'text-info',
        // A state reset just re-runs the same cached, rejected import promise,
        // trapping the user — only a full page reload recovers. So we expose
        // the dedicated "Reload page" button instead of the no-op retry.
        canRetry: false,
        canGoHome: false,
      };
    }

    // Network errors
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('networkerror')
    ) {
      return {
        type: 'network',
        title: t('errorBoundary.network.title'),
        message: t('errorBoundary.network.message'),
        icon: WifiOff,
        iconColor: 'text-warning',
        canRetry: true,
        canGoHome: true,
      };
    }

    // Authentication errors
    if (
      errorMessage.includes('auth') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('forbidden') ||
      errorMessage.includes('401') ||
      errorMessage.includes('403')
    ) {
      return {
        type: 'auth',
        title: t('errorBoundary.auth.title'),
        message: t('errorBoundary.auth.message'),
        icon: Lock,
        iconColor: 'text-danger',
        canRetry: false,
        canGoHome: true,
      };
    }

    // Data/API errors
    if (
      errorMessage.includes('json') ||
      errorMessage.includes('parse') ||
      errorMessage.includes('400') ||
      errorMessage.includes('500')
    ) {
      return {
        type: 'data',
        title: t('errorBoundary.data.title'),
        message: t('errorBoundary.data.message'),
        icon: Database,
        iconColor: 'text-warning',
        canRetry: true,
        canGoHome: true,
      };
    }

    // Unknown/generic errors
    return {
      type: 'unknown',
      title: t('errorBoundary.unknown.title'),
      message: error.message || t('errorBoundary.unknown.message'),
      icon: Bug,
      iconColor: 'text-danger',
      canRetry: true,
      canGoHome: true,
    };
  }

  /**
   * Reset the boundary when the route changes (UX: a crashed screen must not
   * stay stuck after the user navigates elsewhere via the persistent shell).
   */
  componentDidUpdate(prevProps: Props) {
    if (
      this.state.hasError &&
      this.props.resetKey !== undefined &&
      this.props.resetKey !== prevProps.resetKey
    ) {
      this.handleReset();
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Снять загрузочный сплеш — но ТОЛЬКО пока он есть. Он перекрывает
    // страницу целиком, и с тех пор, как момент готовности объявляет сам экран
    // (см. hooks/useAppReady), упавший до этого экран оставлял бы человека
    // смотреть на сплеш все 8 секунд страховочного таймаута и только потом
    // показал бы ошибку. Здесь показывать уже есть что: экран ошибки.
    //
    // Проверка на наличие узла — не перестраховка: этот компонент стоит в
    // дереве восемь раз, в том числе вокруг отдельных панелей уже показанного
    // приложения, и без неё падение любой из них объявляло бы готовность всему
    // документу спустя минуты после загрузки.
    //
    // Узел живёт ещё ~250 мс после первого `app:ready` — столько идёт
    // затухание, — так что в это окно признак ещё срабатывает. Повтор
    // безобиден: слушатель в index.html подписан с `{ once: true }` и к этому
    // моменту уже снят. Связь между файлами держит boot-splash-handoff.test.ts.
    if (typeof document !== 'undefined' && document.getElementById('boot-splash')) {
      window.dispatchEvent(new Event('app:ready'));
    }

    // Log to Sentry with structured logging (automatically falls back to console in DEV)
    logError(
      'error_boundary.caught_error',
      {
        component: 'ErrorBoundary',
        componentStack: errorInfo.componentStack,
      },
      error
    );

    // Categorize error for better UX
    const errorMetadata = this.categorizeError(error);

    // Send to error tracking service (Sentry) if configured.
    // `contexts.custom` is OUR OWN field, not a standard Sentry field the
    // shared event masker (sentry-event-mask.ts) inspects — so unlike
    // `event.request.url`/`transaction`/etc., nothing downstream would ever
    // clean this up. The full href (including query string, and any secret
    // path segment such as an invitation token — see
    // docs/audit/2026-07-31-egress-ui-worker-sentry.md §5.2) must not be
    // captured here in the first place. The masked PATH still identifies
    // which route crashed, which is what this field is for.
    captureException(error, {
      componentStack: errorInfo.componentStack,
      errorType: errorMetadata.type,
      errorTitle: errorMetadata.title,
      errorMessage: errorMetadata.message,
      userAgent: navigator.userAgent,
      path: maskInvitationTokenInPath(window.location.pathname),
    });

    this.setState({ errorInfo, errorMetadata });
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorMetadata: undefined,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const t = i18n.t.bind(i18n);
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Get error metadata (defaults to unknown if not set)
      const metadata = this.state.errorMetadata || {
        type: 'unknown',
        title: i18n.t('errorBoundary.unknown.title'),
        message: this.state.error?.message || i18n.t('errorBoundary.unknown.message'),
        icon: AlertTriangle,
        iconColor: 'text-danger',
        canRetry: true,
        canGoHome: true,
      };

      const Icon = metadata.icon;

      // Enhanced fallback UI with categorized errors
      return (
        <div
          className="flex min-h-[400px] flex-col items-center justify-center p-8"
          role="alert"
        >
          {/* Error Icon */}
          <Icon className={`h-16 w-16 ${metadata.iconColor}`} aria-hidden="true" />

          {/* Error Title */}
          <h2 className="mt-6 text-2xl font-bold text-fg">{metadata.title}</h2>

          {/* Error Message */}
          <p className="mt-3 max-w-lg text-center text-base text-fg">
            {metadata.message}
          </p>

          {/* Error Type Badge (Development) */}
          {import.meta.env.DEV && (
            <div className="mt-4">
              <span className="inline-flex items-center rounded-full bg-bg-elevated px-3 py-1 text-xs font-medium text-fg-muted border border-border">
                {t('errorBoundary.devErrorType', { type: metadata.type })}
              </span>
            </div>
          )}

          {/* Show stack trace in development only */}
          {import.meta.env.DEV && this.state.errorInfo && (
            <details className="mt-6 max-w-3xl w-full rounded-card border border-border bg-bg-elevated p-4">
              <summary className="cursor-pointer text-sm font-medium text-fg hover:text-primary">
                {t('errorBoundary.devDetailsTitle')}
              </summary>
              <div className="mt-3 space-y-2">
                <div>
                  <p className="text-xs font-semibold text-fg mb-1">
                    {t('errorBoundary.devErrorMessage')}
                  </p>
                  <pre className="overflow-auto text-xs text-fg-muted bg-bg p-2 rounded border border-border">
                    {this.state.error?.message}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-semibold text-fg mb-1">
                    {t('errorBoundary.devStackTrace')}
                  </p>
                  <pre className="overflow-auto text-xs text-fg-muted bg-bg p-2 rounded border border-border max-h-40">
                    {this.state.error?.stack}
                  </pre>
                </div>
                <div>
                  <p className="text-xs font-semibold text-fg mb-1">
                    {t('errorBoundary.devComponentStack')}
                  </p>
                  <pre className="overflow-auto text-xs text-fg-muted bg-bg p-2 rounded border border-border max-h-40">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              </div>
            </details>
          )}

          {/* Action Buttons */}
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {/* Retry button */}
            {metadata.canRetry && (
              <Button onClick={this.handleReset} variant="outline" size="lg">
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('common:common.retry')}
              </Button>
            )}

            {/* Reload button for chunk errors */}
            {metadata.type === 'chunk' && (
              <Button onClick={this.handleReload} variant="default" size="lg">
                <RefreshCw className="mr-2 h-4 w-4" />
                {t('errorBoundary.reloadPage')}
              </Button>
            )}

            {/* Home button */}
            {metadata.canGoHome && (
              <Button onClick={this.handleGoHome} variant="default" size="lg">
                <Home className="mr-2 h-4 w-4" />
                {t('errorBoundary.goToDashboard')}
              </Button>
            )}
          </div>

          {/* Support Contact (Production) */}
          {!import.meta.env.DEV && (
            <p className="mt-8 text-xs text-fg-subtle">
              {t('errorBoundary.supportPrompt')}{' '}
              <a
                href="mailto:support@auspex.company"
                className="text-primary hover:underline"
              >
                support@auspex.company
              </a>
            </p>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
